import {
  IonButton,
  IonCard,
  IonCardContent,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonModal,
  IonRadio,
  IonRadioGroup,
} from '@ionic/react'
import './registration.scss'
import BasePage from 'pages/base-page/base-page.page'
import { useTranslation } from 'react-i18next'
import { useCitizenship, useComuni, useProfileTypes, useProvince } from 'hooks'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useState } from 'react'
import { profileService, routeService } from 'services'
import { Redirect } from 'react-router'
import i18next from 'i18n'
import Select from 'react-select'
import Calendar from 'react-calendar'

const validationSchema = Yup.object()
  .shape({
    lastname: Yup.string().max(30, i18next.t('form_validator.longLastname')).required(i18next.t('form_validator.required')),
    name: Yup.string().max(30, i18next.t('form_validator.longName')).required(i18next.t('form_validator.required')),
    birth_date: Yup.string().required(i18next.t('form_validator.required')),
    country: Yup.string().required(i18next.t('form_validator.required')),
    place: Yup.string().required(i18next.t('form_validator.required')),
    citizenship: Yup.string().required(i18next.t('form_validator.required')),
    userprofile: Yup.string().required(i18next.t('form_validator.required')),
    phone: Yup.string()
      .required(i18next.t('form_validator.required'))
      .max(15, i18next.t('form_validator.longPhone'))
      .matches(/^\+[0-9]{1,14}$/, i18next.t('form_validator.error_phone_prefix')),
    fiscalcode: Yup.string().length(16, i18next.t('form_validator.error_fiscalcode')).required(i18next.t('form_validator.required')),
    mail: Yup.string().email(i18next.t('form_validator.error_mail')).required(i18next.t('form_validator.required')),
    confirm_mail: Yup.string()
      .oneOf([Yup.ref('mail'), ''], i18next.t('form_validator.match_mail'))
      .required(i18next.t('form_validator.required')),
    gender: Yup.string().required(i18next.t('form_validator.required')),
    privacy: Yup.string().required(i18next.t('form_validator.required')),
  })
  .test('invalid-citizenship', i18next.t('form_validator.invalid_citizenship'), function (values) {
    if (values.country === 'EE' && values.citizenship === 'X001') {
      return this.createError({
        path: 'citizenship',
        message: i18next.t('form_validator.invalid_citizenship'),
      })
    }
    return true
  })

const Registration: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [redirect, setRedirect] = useState<string | undefined>(undefined)
  const [provincia, setProvincia] = useState<string | undefined>(undefined)
  const [datesPickerModalOpen, setDatesPickerModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const formatDate = (date: Date, type: 'frontend' | 'backend') => {
    if (!date) return ''
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    if (type === 'frontend') {
      return `${day}/${month}/${year}`
    } else {
      return `${year}-${month}-${day}`
    }
  }
  const handleDateChange = (date: Date) => {
    setSelectedDate(formatDate(date, 'frontend'))
    formik.setFieldValue('birth_date', formatDate(date, 'backend'))
    setDatesPickerModalOpen(false)
  }
  const comuni = useComuni(provincia)
  const filteredComuni = provincia === 'EE' ? comuni.filter((c) => c.id !== 'X001') : comuni
  const province = useProvince()
  const citizenship = useCitizenship().filter((c) => c.id !== 'X001')
  const profileTypes = useProfileTypes()
  const { t } = useTranslation()
  const formik = useFormik({
    initialValues: {
      lastname: '',
      name: '',
      birth_date: '',
      gender: '',
      country: '',
      place: '',
      citizenship: '',
      userprofile: '',
      mail: '',
      phone: '',
      privacy: '',
      fiscalcode: '',
      confirm_mail: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true)
      profileService.register(
        values.name.toUpperCase(),
        values.lastname.toUpperCase(),
        values.gender,
        values.fiscalcode.toUpperCase(),
        values.birth_date,
        values.place,
        values.citizenship,
        values.userprofile,
        values.mail,
        values.phone,
        () => {
          setLoading(false)
          setRedirect(routeService.generateRoutePath('/registration-success'))
        },
        () => {
          setLoading(false)
          setRedirect(routeService.generateRoutePath('/error') + '?errorCode=registration')
        },
      )
    },
  })

  if (!!redirect) {
    return <Redirect to={redirect}></Redirect>
  }

  return (
    <BasePage pageClass="edisu-page-registration" bgColor="#EDF2F5">
      <IonLoading isOpen={loading} message={t('loading')} />
      <div className="edisu-registration-grid">
        <h2> {t('registration_title')}</h2>
        <IonCard className="edisu-registration-card">
          <IonCardContent>
            <form onSubmit={formik.handleSubmit} className="container">
              <div className="row">
                <div className="field">
                  <IonLabel className="form-label">{t('lastname').toUpperCase()}</IonLabel>
                  <IonInput
                    value={formik.values.lastname.toUpperCase()}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="input"
                    placeholder={t('lastname')}
                    name="lastname"
                  ></IonInput>
                  <IonLabel className={formik.touched.lastname && formik.errors.lastname ? 'form-label-error' : 'hidden'}>
                    {formik.touched.lastname && formik.errors.lastname ? formik.errors.lastname : t('lastname')}
                  </IonLabel>
                </div>

                <div className="field">
                  <IonLabel className={'form-label'}>{t('name').toUpperCase()}</IonLabel>
                  <IonInput
                    value={formik.values.name.toUpperCase()}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="input"
                    placeholder={t('name')}
                    name="name"
                  ></IonInput>
                  <IonLabel className={formik.touched.name && formik.errors.name ? 'form-label-error' : 'hidden'}>
                    {formik.touched.name && formik.errors.name ? formik.errors.name : t('name')}
                  </IonLabel>
                </div>
              </div>
              <div className="row">
                <div className="field date">
                  <IonLabel className={'form-label'}>{t('birth_date').toUpperCase()}</IonLabel>
                  <br />
                  <input
                    value={selectedDate || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onClick={() => setDatesPickerModalOpen(true)}
                    name="birth_date"
                    className="datepicker"
                    placeholder={t('birth_date')}
                  ></input>
                  <IonLabel className={formik.touched.birth_date && formik.errors.birth_date ? 'form-label-error' : 'hidden'}>
                    {formik.touched.birth_date && formik.errors.birth_date ? formik.errors.birth_date : t('birth_date')}
                  </IonLabel>
                  {/* Modale del Calendario */}
                  <IonModal className="custom-modal" isOpen={datesPickerModalOpen} onDidDismiss={() => setDatesPickerModalOpen(false)}>
                    <Calendar onChange={(date: Date) => handleDateChange(date)} />
                    <IonButton onClick={() => setDatesPickerModalOpen(false)}>{t('close').toUpperCase()}</IonButton>
                  </IonModal>
                </div>

                <div className="field">
                  <IonLabel className="form-label">{t('gender').toUpperCase()}</IonLabel>

                  <IonRadioGroup onIonChange={(e) => (formik.values.gender = e.detail.value)} className="radioGroup">
                    <IonItem lines="none" className="itemMF">
                      <IonLabel className="labelMF">M</IonLabel>
                      <IonRadio value="M"></IonRadio>
                    </IonItem>
                    <IonItem lines="none" className="itemMF">
                      <IonLabel className="labelMF">F</IonLabel>
                      <IonRadio value="F"></IonRadio>
                    </IonItem>
                  </IonRadioGroup>
                  <IonLabel className={formik.touched.gender && formik.errors.gender ? 'form-label-error' : 'hidden'}>
                    {formik.touched.gender && formik.errors.gender ? formik.errors.gender : t('gender')}
                  </IonLabel>
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('birth_country').toUpperCase()}</IonLabel>
                  <Select
                    name="country"
                    options={
                      province
                        ? province.map((c) => ({
                            value: c.id,
                            label: c.descrizione,
                          }))
                        : []
                    }
                    onChange={(e) => {
                      formik.setFieldValue('country', e!.value)
                      formik.errors.country = ''
                      setProvincia(e!.value)
                    }}
                    onBlur={(e) => {
                      formik.touched.country = true
                      formik.handleBlur(e)
                    }}
                    className="dropdown"
                    placeholder={t('select_province')}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                  <IonLabel className="note" color="dark">
                    {t('warning')}
                  </IonLabel>
                  <br />
                  <IonLabel className={formik.touched.country && formik.errors.country ? 'form-label-error' : 'hidden'}>
                    {formik.touched.country && formik.errors.country ? formik.errors.country : t('birth_country')}
                  </IonLabel>
                </div>
                <div className="field">
                  <IonLabel className={'form-label'}>{t('birthplace').toUpperCase()}</IonLabel>
                  <Select
                    name="place"
                    options={
                      filteredComuni
                        ? filteredComuni.map((c) => ({
                            value: c.id,
                            label: c.descrizione,
                          }))
                        : []
                    }
                    onChange={(e) => {
                      formik.values.place = e!.value
                      formik.errors.place = ''
                    }}
                    onBlur={(e) => {
                      formik.touched.place = true
                      formik.handleBlur(e)
                    }}
                    className="dropdown"
                    placeholder={t('select_municipality')}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                  <IonLabel className={formik.touched.place && formik.errors.place ? 'form-label-error' : 'hidden'}>
                    {formik.touched.place && formik.errors.place ? formik.errors.place : t('birthplace')}
                  </IonLabel>
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('fiscalcode').toUpperCase()}</IonLabel>
                  <IonInput
                    value={formik.values.fiscalcode.toUpperCase()}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="fiscalcode"
                    className="input"
                    placeholder={t('your_fiscalcode')}
                    onCopy={(e) => {
                      e.preventDefault()
                      return false
                    }}
                  ></IonInput>
                  <IonLabel className={formik.touched.fiscalcode && formik.errors.fiscalcode ? 'form-label-error' : 'hidden'}>
                    {formik.touched.fiscalcode && formik.errors.fiscalcode ? formik.errors.fiscalcode : t('fiscalcode')}
                  </IonLabel>
                </div>
                <div className="field">
                  <IonLabel className="form-label">
                    <br />
                    <a href="https://www.codicefiscale.com/" target="_blank" rel="noopener noreferrer">
                      {t('link_fiscalcode')}
                    </a>
                  </IonLabel>
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('citizenship').toUpperCase()}</IonLabel>
                  <Select
                    name="citizenship"
                    options={
                      citizenship
                        ? citizenship.map((c) => ({
                            value: c.id,
                            label: c.descrizione,
                          }))
                        : []
                    }
                    onChange={(e) => {
                      formik.values.citizenship = e!.value
                      formik.errors.citizenship = ''
                    }}
                    onBlur={(e) => {
                      formik.touched.citizenship = true
                      formik.handleBlur(e)
                    }}
                    className="dropdown"
                    placeholder={t('select_state')}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                  <IonLabel className={formik.touched.citizenship && formik.errors.citizenship ? 'form-label-error' : 'hidden'}>
                    {formik.touched.citizenship && formik.errors.citizenship ? formik.errors.citizenship : t('citizenship')}
                  </IonLabel>
                </div>

                <div className="field">
                  <IonLabel className={'form-label'}>{t('user_profile')}</IonLabel>
                  <Select
                    name="userprofile"
                    options={
                      profileTypes
                        ? profileTypes.map((c) => ({
                            value: c.id,
                            label: c.descrizione.toUpperCase(),
                          }))
                        : []
                    }
                    onChange={(e) => {
                      formik.values.userprofile = e!.value.toString().toUpperCase()
                      formik.errors.userprofile = ''
                    }}
                    onBlur={(e) => {
                      formik.touched.citizenship = true
                      formik.handleBlur(e)
                    }}
                    className="dropdown"
                    placeholder={t('select_profile')}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                  <IonLabel className={formik.touched.userprofile && formik.errors.userprofile ? 'form-label-error' : 'hidden'}>
                    {formik.touched.userprofile && formik.errors.userprofile ? formik.errors.userprofile : t('user_profile')}
                  </IonLabel>
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('mail')}</IonLabel>
                  <IonInput
                    type="email"
                    value={formik.values.mail}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="mail"
                    className="input"
                    placeholder={t('your_mail')}
                    onCopy={(e) => {
                      e.preventDefault()
                      return false
                    }}
                  ></IonInput>
                  <IonLabel className="form-label note">{'*' + t('mail_text1')}</IonLabel>
                  <br />
                  <IonLabel className={formik.touched.mail && formik.errors.mail ? 'form-label-error' : 'hidden'}>
                    {formik.touched.mail && formik.errors.mail ? formik.errors.mail : t('mail')}
                  </IonLabel>
                </div>
                <div className="field">
                  <IonLabel className={'form-label'}>{t('confirm_mail')}</IonLabel>
                  <IonInput
                    type="email"
                    value={formik.values.confirm_mail}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="confirm_mail"
                    className="input"
                    placeholder={t('confirm_your_mail')}
                    onPaste={(e) => {
                      e.preventDefault()
                      return false
                    }}
                  ></IonInput>
                  <IonLabel className={formik.touched.confirm_mail && formik.errors.confirm_mail ? 'form-label-error' : 'hidden'}>
                    {formik.touched.confirm_mail && formik.errors.confirm_mail ? formik.errors.confirm_mail : t('mail')}
                  </IonLabel>
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('phone_number')}</IonLabel>
                  <IonInput
                    value={formik.values.phone}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="phone"
                    className="input"
                    placeholder={t('your_phone')}
                  ></IonInput>
                  <IonLabel className={formik.touched.phone && formik.errors.phone ? 'form-label-error' : 'hidden'}>
                    {formik.touched.phone && formik.errors.phone ? formik.errors.phone : t('phone_number')}
                  </IonLabel>
                </div>
              </div>

              <div>
                <br />
                <IonLabel className="form-label">{t('mail_text2')}</IonLabel>
                <br />
                <IonLabel className="form-label">
                  <br />
                  {t('privacy_intro') + ' '}
                  <a href="/assets/pdf/Privacy.pdf" target="_blank" rel="noopener noreferrer">
                    {t('privacy')}
                  </a>
                </IonLabel>
                <IonRadioGroup onIonChange={(e) => (formik.values.privacy = e.detail.value)} allowEmptySelection={true} className="privacy">
                  <IonItem lines="none" className="itemRadio">
                    <IonRadio value="accepted"></IonRadio>
                    <IonLabel className="ion-text-wrap labelRadio">{t('privacy_text1')}</IonLabel>
                  </IonItem>
                </IonRadioGroup>
                <IonLabel className={formik.touched.privacy && formik.errors.privacy ? 'form-label-error' : 'hidden'}>
                  {formik.touched.privacy && formik.errors.privacy ? formik.errors.privacy : t('privacy')}
                </IonLabel>
              </div>
              <div className="buttonRegister">
                <IonButton type="submit">{t('register')}</IonButton>
              </div>
            </form>
          </IonCardContent>
        </IonCard>
      </div>
    </BasePage>
  )
}

export default Registration
